import { useEffect, useState, type FormEvent } from 'react';
import {
  Button,
  Checkbox,
  cn,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@eureca/ui';
import { DISABILITY_OPTIONS } from './disabilities-seed';

export interface DisabilitySelection {
  selectedIds: string[];
  otherText: string;
}

export interface DisabilityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultValue?: DisabilitySelection;
  onSubmit: (value: DisabilitySelection) => void;
}

const EMPTY: DisabilitySelection = { selectedIds: [], otherText: '' };

export function DisabilityDialog({
  open,
  onOpenChange,
  defaultValue,
  onSubmit,
}: DisabilityDialogProps) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [otherText, setOtherText] = useState('');

  useEffect(() => {
    if (open) {
      const initial = defaultValue ?? EMPTY;
      setSelectedIds([...initial.selectedIds]);
      setOtherText(initial.otherText);
    }
  }, [open, defaultValue]);

  const otherSelected = selectedIds.includes('outra');
  const canSubmit = selectedIds.length > 0 && (!otherSelected || otherText.trim().length > 0);

  function handleToggle(id: string) {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((entry) => entry !== id) : [...prev, id]
    );
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canSubmit) return;
    onSubmit({ selectedIds, otherText: otherText.trim() });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        className="gap-0 p-0 lg:w-full lg:max-w-[423px] lg:rounded-xl"
      >
        <DialogHeader className="gap-1.5 px-6 pt-6">
          <DialogTitle className="text-lg font-semibold leading-none tracking-[-0.025em] text-foreground">
            Tipo de deficiência
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="flex flex-col gap-3 px-6 pt-3 pb-8">
            {DISABILITY_OPTIONS.map((option) => {
              const isChecked = selectedIds.includes(option.id);
              return (
                <div key={option.id} className="flex flex-col gap-2">
                  <label
                    className={cn(
                      'flex cursor-pointer flex-row items-center gap-3 rounded-xl border bg-card px-4 py-3 text-sm font-medium transition-colors',
                      isChecked
                        ? 'border-primary bg-primary/10 text-foreground'
                        : 'border-border text-foreground hover:border-primary'
                    )}
                  >
                    <Checkbox
                      checked={isChecked}
                      onCheckedChange={() => handleToggle(option.id)}
                      className="size-4"
                      data-testid={`disability-${option.id}`}
                    />
                    <span>{option.label}</span>
                  </label>
                  {option.freeText && isChecked ? (
                    <Input
                      value={otherText}
                      onChange={(event) => setOtherText(event.target.value)}
                      placeholder="Especifique"
                      data-testid="disability-other-text"
                    />
                  ) : null}
                </div>
              );
            })}
          </div>
          <DialogFooter className="px-6 pb-6">
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancelar
              </Button>
            </DialogClose>
            <Button type="submit" disabled={!canSubmit} data-testid="disability-dialog-submit">
              Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
